/**
 * RetryDialog - Dialog for retrying a failed registration/upgrade session
 * Shows session details, retry history, and requires mandatory notes
 */

import type { RegistrationSession, RetryAttempt } from '@/application/queries/registrationSessions/GetRegistrationSessionsQuery';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@/components/ui';
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { formatDateTimeByPrefs } from '@utils/format-locale';
import { useUserPreferences } from '@/components/features/settings/UserSettings';

// ============================================================================
// Types
// ============================================================================

interface RetryDialogProps {
  session: RegistrationSession;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRetry: (notes: string) => Promise<void>;
}

// ============================================================================
// Component
// ============================================================================

export const RetryDialog: React.FC<RetryDialogProps> = ({
  session,
  open,
  onOpenChange,
  onRetry,
}) => {
  const { t } = useTranslation(NS.DEVELOPER_PLAN);
  const [retryNotes, setRetryNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleRetry = async () => {
    if (!retryNotes.trim()) {
      setSubmitError(t('RetryDialog_Error_NotesRequired', 'Retry notes are required'));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await onRetry(retryNotes);
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : t('RetryDialog_Error_Unknown', 'Retry failed')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('RetryDialog_Title', 'Retry Registration Session')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Session Summary */}
          <Card>
            <CardHeader className="pb-2">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {t('RetryDialog_Section_Details', 'Session Details')}
              </h3>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className="text-muted-foreground">
                  {t('RetryDialog_Label_Email', 'Email')}
                </dt>
                <dd className="font-medium">{session.contactEmail || '—'}</dd>

                <dt className="text-muted-foreground">
                  {t('RetryDialog_Label_Company', 'Company')}
                </dt>
                <dd className="font-medium">{session.companyName || '—'}</dd>

                <dt className="text-muted-foreground">
                  {t('RetryDialog_Label_Type', 'Type')}
                </dt>
                <dd className="font-medium capitalize">{session.sessionType}</dd>

                <dt className="text-muted-foreground">
                  {t('RetryDialog_Label_Plan', 'Plan')}
                </dt>
                <dd className="font-medium">{session.plan}</dd>

                <dt className="text-muted-foreground">
                  {t('RetryDialog_Label_NetworkId', 'Network ID')}
                </dt>
                <dd className="font-medium">{session.networkId ?? 'Not created'}</dd>

                <dt className="text-muted-foreground">
                  {t('RetryDialog_Label_Checkpoint', 'Failed Checkpoint')}
                </dt>
                <dd>
                  <Badge variant="destructive">{session.errorCheckpoint || 'Unknown'}</Badge>
                </dd>

                <dt className="text-muted-foreground col-span-2 mt-2">
                  {t('RetryDialog_Label_Error', 'Error')}
                </dt>
                <dd className="col-span-2 text-sm text-red-600 bg-red-50 p-2 rounded-md">
                  {session.errorMessage || 'No error message'}
                </dd>
              </dl>
            </CardContent>
          </Card>

          {/* Retry History */}
          {session.retryHistory.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  {t('RetryDialog_Section_History', 'Retry History')} ({session.retryHistory.length})
                </h3>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {session.retryHistory.map((attempt, idx) => (
                    <RetryAttemptItem key={idx} attempt={attempt} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Retry Notes Input */}
          <div className="space-y-2">
            <Label htmlFor="retry-notes" className="flex items-center gap-1">
              {t('RetryDialog_Label_Notes', 'Why are you retrying this session?')}
              <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="retry-notes"
              value={retryNotes}
              onChange={(e) => setRetryNotes(e.target.value)}
              placeholder={t(
                'RetryDialog_Notes_Placeholder',
                'E.g., Fixed ECGridOS API timeout issue, verified network quota available'
              )}
              rows={3}
              required
              className="resize-none"
            />
          </div>

          {/* Error Message */}
          {submitError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t('RetryDialog_Error_Title', 'Error')}</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          {/* Warning Alert */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {t('RetryDialog_Warning_Title', 'Retry will resume from checkpoint')}
            </AlertTitle>
            <AlertDescription>
              {t(
                'RetryDialog_Warning_Description',
                'This will resume provisioning from: {{checkpoint}}. Ensure the underlying issue has been resolved before retrying.',
                { checkpoint: session.errorCheckpoint || 'Unknown' }
              )}
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('RetryDialog_Button_Cancel', 'Cancel')}
          </Button>
          <Button onClick={handleRetry} disabled={isSubmitting || !retryNotes.trim()}>
            {isSubmitting
              ? t('RetryDialog_Button_Retrying', 'Retrying...')
              : t('RetryDialog_Button_Confirm', 'Confirm Retry')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Retry Attempt Item
// ============================================================================

interface RetryAttemptItemProps {
  attempt: RetryAttempt;
}

const RetryAttemptItem: React.FC<RetryAttemptItemProps> = ({ attempt }) => {
  const { timezone } = useUserPreferences();
  const formatDate = (dateStr: string) => formatDateTimeByPrefs(dateStr, timezone);

  return (
    <div className="flex gap-3 border-l-2 border-muted pl-4 pb-2">
      <div className="flex-shrink-0 mt-1">
        {attempt.outcome === 'success' ? (
          <CheckCircle className="h-4 w-4 text-green-500" />
        ) : (
          <XCircle className="h-4 w-4 text-red-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{formatDate(attempt.attemptedAt)}</span>
          <span className="text-muted-foreground">by</span>
          <span className="font-medium truncate">{attempt.attemptedBy}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{attempt.notes}</p>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant={attempt.outcome === 'success' ? 'default' : 'destructive'}>
            {attempt.outcome}
          </Badge>
          {attempt.newError && (
            <span className="text-sm text-red-600 truncate">{attempt.newError}</span>
          )}
        </div>
      </div>
    </div>
  );
};
